import { FC, useState, useRef, useMemo } from "react";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import SaveIcon from "@mui/icons-material/Save";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { TEXT, disabledButtonStyle } from "./const";

interface SaveWorkspaceButtonProps {
  userCanSave: boolean;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  onSave: () => void;
  onSaveAsNew: () => void;
}

export const SaveWorkspaceButton: FC<SaveWorkspaceButtonProps> = ({
  userCanSave,
  hasUnsavedChanges,
  isSaving,
  onSave,
  onSaveAsNew,
}) => {
  const [openSaveMenu, setOpenSaveMenu] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggleSaveMenu = () => {
    setOpenSaveMenu((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpenSaveMenu(false);
  };

  const handleSaveAsNewClick = () => {
    onSaveAsNew();
    setOpenSaveMenu(false);
  };

  const isSaveDisabled = useMemo(
    () => !hasUnsavedChanges || isSaving,
    [hasUnsavedChanges, isSaving]
  );

  const saveButtonIcon = useMemo(
    () =>
      isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />,
    [isSaving]
  );

  const saveButtonText = isSaving ? TEXT.SAVING : TEXT.SAVE_WORKSPACE;

  const getTooltipMessage = useMemo(() => {
    if (!hasUnsavedChanges) return TEXT.NO_CHANGES_TOOLTIP;
    return "";
  }, [hasUnsavedChanges]);

  // When user can't save, show only Save As New button
  if (!userCanSave) {
    return (
      <Button
        variant="contained"
        onClick={onSaveAsNew}
        size="small"
        color="primary"
        startIcon={<SaveIcon />}
      >
        {TEXT.SAVE_AS_NEW_WORKSPACE}
      </Button>
    );
  }

  // When user can save, show split button
  return (
    userCanSave && (
      <>
        <Tooltip title={isSaveDisabled ? getTooltipMessage : ""} arrow>
          <span>
            <ButtonGroup
              id="save-workspace-button-group"
              disableElevation
              variant="contained"
              ref={anchorRef}
              aria-label="save project button group"
            >
              <Button
                id="save-workspace-button"
                data-testid="workspace-save-btn"
                onClick={() => {
                  setOpenSaveMenu(false);
                  onSave();
                }}
                size="small"
                color="primary"
                disabled={isSaveDisabled}
                startIcon={saveButtonIcon}
                sx={isSaveDisabled ? disabledButtonStyle : {}}
              >
                {saveButtonText}
              </Button>
              <Button
                id="save-workspace-button-dropdown"
                data-testid="workspace-save-menu-dropdown"
                size="small"
                color="primary"
                disabled={isSaveDisabled}
                aria-controls={openSaveMenu ? "save-menu" : undefined}
                aria-expanded={openSaveMenu ? "true" : undefined}
                aria-label="select save option"
                aria-haspopup="menu"
                onClick={handleToggleSaveMenu}
                sx={isSaveDisabled ? disabledButtonStyle : {}}
              >
                <ArrowDropDownIcon />
              </Button>
            </ButtonGroup>
          </span>
        </Tooltip>

        <Popper
          sx={{ zIndex: 99 }}
          open={openSaveMenu}
          anchorEl={anchorRef.current}
          role={undefined}
          placement="bottom-end"
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center bottom" : "start bottom",
              }}
            >
              <Paper elevation={2}>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="save-menu" autoFocusItem>
                    <MenuItem
                      data-testid="workspace-save-as-new-btn"
                      onClick={handleSaveAsNewClick}
                    >
                      <FileCopyIcon fontSize="small" sx={{ mr: 1 }} />
                      {TEXT.SAVE_AS_NEW_WORKSPACE}
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </>
    )
  );
};
